import React from 'react';
import { MapPin, CheckCircle, XCircle, Bell, Plus, AlertTriangle, Minus } from 'lucide-react';
import type { CarrierProfile as CarrierProfileType } from '../../types';

interface CarrierProfileProps {
  profile: CarrierProfileType;
  healthScore: number;
  riskLevel: { level: string; color: string };
  sixMonthCounts: {
    crashes: number;
    violations: number;
    inspections: {
      total: number;
      failed: number;
      clean: number;
    };
  };
  isMonitored?: boolean;
  onAddToWatchlist?: () => void;
  onRefresh?: () => void;
  onRemoveFromWatchlist?: () => void;
  addingToWatchlist?: boolean;
  loading?: boolean;
  className?: string;
}

export const CarrierProfile: React.FC<CarrierProfileProps> = ({
  profile,
  healthScore,
  riskLevel,
  sixMonthCounts,
  isMonitored = false,
  onAddToWatchlist,
  addingToWatchlist = false,
  className = ''
}) => {
  return (
    <>
      {/* Profile Header - Clean Simple Design */}
      <div className={`card-modern fade-in-up section-spacing ${className}`} style={{ animationDelay: '0.1s' }}>
        <div className="flex items-start justify-between flex-wrap gap-6">
          {/* Left: DOT and Company Info */}
          <div className="flex items-start gap-4">
            {/* DOT Number */}
            <div className="stat-box min-w-[100px]">
              <div className="text-lg font-bold text-gray-900">{profile.dotNumber}</div>
              <div className="stat-label">DOT NUMBER</div>
            </div>

            {/* EIN (if on file - fetched on-demand from FMCSA, see ein-lookup.service.ts) */}
            {profile.ein && (
              <div className="stat-box min-w-[100px]">
                <div className="text-lg font-bold text-gray-900">{profile.ein}</div>
                <div className="stat-label">EIN</div>
              </div>
            )}

            {/* MC Number (if available) */}
            {profile && ((profile as CarrierProfileType & { mcNumber?: string; mc_number?: string }).mcNumber || (profile as CarrierProfileType & { mc_number?: string }).mc_number) && (
              <div className="stat-box min-w-[100px]">
                <div className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>{(profile as CarrierProfileType & { mcNumber?: string; mc_number?: string }).mcNumber || (profile as CarrierProfileType & { mc_number?: string }).mc_number}</div>
                <div className="stat-label">MC NUMBER</div>
              </div>
            )}

            {/* Company Info */}
            <div>
              <h1 className="heading-1 uppercase mb-1">
                {profile.legalName}
              </h1>
              {profile.dbaName && profile.dbaName !== profile.legalName && (
                <p className="text-muted mb-1">
                  DBA: {profile.dbaName}
                </p>
              )}
              <div className="flex items-center gap-1 text-gray-500 text-sm mb-2">
                <MapPin size={14} />
                <span>{profile.phyCity}, {profile.phyState}</span>
              </div>
              {/* Status Badges - inline */}
              <div className="flex gap-2 flex-wrap">
                <span className={`badge-modern ${
                  (profile as any).authorizationType === 'Not Authorized'
                    ? 'badge-danger'
                    : (profile as any).authorizationType === 'Private Carrier' || (profile as any).authorizationType === 'Exempt Carrier'
                    ? 'bg-gray-100 text-gray-700 border-gray-300'
                    : (profile as any).authorizationType === 'Dual Authority'
                    ? 'badge-info'
                    : 'badge-success'
                } inline-flex items-center gap-1`}>
                  {(profile as any).authorizationType === 'Not Authorized' ? <XCircle size={12} /> :
                   (profile as any).authorizationType === 'Private Carrier' || (profile as any).authorizationType === 'Exempt Carrier' ? <Minus size={12} /> :
                   <CheckCircle size={12} />}
                  {(profile as any).authorizationType || (profile.allowedToOperate === 'Y' ? 'Authorized' : 'Not Authorized')}
                </span>
                <span className={`badge-modern ${
                  profile.statusCode === 'A'
                    ? 'badge-success'
                    : 'badge-warning'
                }`}>
                  {profile.statusCode === 'A' ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* DNU (Do Not Use) Badge */}
              {profile.dnuStatus && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 16px',
                  background: '#fee2e2',
                  border: '2px solid #dc2626',
                  borderRadius: '8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  maxWidth: 'fit-content'
                }}>
                  <AlertTriangle size={20} style={{ color: '#dc2626' }} />
                  <span style={{ color: '#991b1b', fontWeight: 600, fontSize: '0.875rem' }}>
                    DNU by {profile.dnuStatus.shipper} since{' '}
                    {new Date(profile.dnuStatus.dnuDate).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Health Score and Watchlist */}
          <div className="flex items-start gap-4">
            {/* Health Score */}
            <div className="stat-box min-w-[100px]">
              <div className="text-3xl font-bold text-gray-900">{healthScore}</div>
              <div className="stat-label mb-1">HEALTH SCORE</div>
              <div className="text-muted text-xs">{riskLevel.level}</div>
            </div>

            {/* Add to Watchlist Button */}
            {onAddToWatchlist && (
              <button
                onClick={onAddToWatchlist}
                disabled={addingToWatchlist || isMonitored}
                className={`btn-secondary inline-flex items-center gap-1 text-sm px-4 py-2 ${(addingToWatchlist || isMonitored) ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {addingToWatchlist ? (
                  <>Adding...</>
                ) : isMonitored ? (
                  <>
                    <Bell size={14} />
                    Monitoring
                  </>
                ) : (
                  <>
                    <Plus size={14} />
                    Add to Watchlist
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Key Stats Grid - Simple Table Style with borders */}
      <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.2s' }}>
        <div className="grid-spacious grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
          <div className="stat-box">
            <div className="text-3xl font-bold text-gray-900">{profile.totalDrivers}</div>
            <div className="stat-label">DRIVERS</div>
          </div>
          <div className="stat-box">
            <div className="text-3xl font-bold text-gray-900">{profile.totalPowerUnits}</div>
            <div className="stat-label">POWER UNITS</div>
          </div>
          <div className="stat-box">
            <div className="text-3xl font-bold text-gray-900">{sixMonthCounts.crashes}</div>
            <div className="stat-label">CRASHES (6MO)</div>
          </div>
          <div className="stat-box">
            <div className="text-3xl font-bold text-gray-900">{sixMonthCounts.violations}</div>
            <div className="stat-label">VIOLATIONS (6MO)</div>
          </div>
          <div className="stat-box">
            <div className="text-3xl font-bold text-gray-900">{sixMonthCounts.inspections.total}</div>
            <div className="stat-label mb-1">INSPECTIONS (6MO)</div>
            <div className="text-muted text-sm">
              {sixMonthCounts.inspections.failed} Failed | {sixMonthCounts.inspections.clean} Clean
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
